import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, Typography, Alert, message, Grid } from "antd";
import { validate as validateRut, clean as cleanRut, format as formatRut } from "rut.js";
import { portalRegister } from "../../api/portalService";
import { AUTH_TOKEN_KEY, AUTH_USER_ID_KEY, AUTH_REFRESH_TOKEN_KEY } from "../../constants";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PortalRegister = () => {
  const navigate  = useNavigate();
  const screens   = useBreakpoint();
  const isMobile  = !screens.md;
  const [form]    = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const onRutBlur = (e) => {
    const raw = e.target.value;
    if (raw && validateRut(raw)) form.setFieldsValue({ rut: formatRut(cleanRut(raw)) });
  };

  const onFinish = async (values) => {
    setLoading(true); setError(null);
    try {
      const cleanedRut = cleanRut(values.rut);
      const { confirm: _, ...rest } = values;
      const res = await portalRegister({ ...rest, rut: cleanedRut });
      const { user, tokens } = res.data;

      const accessToken  = tokens?.access?.token  || tokens?.accessToken;
      const refreshToken = tokens?.refresh?.token || tokens?.refreshToken;

      if (accessToken)  localStorage.setItem(AUTH_TOKEN_KEY, accessToken);
      if (refreshToken) localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
      if (user?.id || user?._id) localStorage.setItem(AUTH_USER_ID_KEY, user.id || user._id);

      message.success(res.data.activated ? "¡Cuenta activada! Bienvenido." : "¡Cuenta creada! Bienvenido.");
      navigate("/portal/profile");
    } catch (e) {
      setError(e?.response?.data?.message || "Error al registrarse. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: isMobile ? "column" : "row" }}>

      {/* Panel decorativo — solo desktop */}
      {!isMobile && (
        <div style={{
          flex: 1,
          background: "linear-gradient(135deg, #1d3557 0%, #457b9d 100%)",
          display: "flex", flexDirection: "column",
          justifyContent: "center", alignItems: "center",
          padding: "2rem", color: "white", textAlign: "center",
        }}>
          <img
            src="/logo.png" alt="Notaría"
            style={{ height: 80, objectFit: "contain", filter: "brightness(0) invert(1)", marginBottom: 24 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <Title level={2} style={{ color: "white", marginBottom: 8 }}>Portal de Clientes</Title>
          <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
            Notaría Juan Carlos Cabezas Cepeda<br />Puerto Montt, Chile
          </Text>
          <div style={{ marginTop: 32, color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.8 }}>
            <p>✓ Accede a documentos notariales</p>
            <p>✓ Pre-llena tus datos en futuros trámites</p>
            <p>✓ Si ya realizaste trámites con nosotros, solo activa tu cuenta</p>
          </div>
        </div>
      )}

      {/* Formulario */}
      <div style={{
        flex: 1,
        background: isMobile ? "#f0f2f5" : "white",
        display: "flex", flexDirection: "column",
        justifyContent: isMobile ? "flex-start" : "center",
        alignItems: "center",
        padding: isMobile ? "32px 20px" : "2rem",
        overflowY: "auto",
        minHeight: isMobile ? "100vh" : "auto",
      }}>

        {/* Logo en mobile */}
        {isMobile && (
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "linear-gradient(135deg, #1d3557, #457b9d)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px",
            }}>
              <img
                src="/logo.png" alt="Notaría"
                style={{ height: 38, objectFit: "contain", filter: "brightness(0) invert(1)" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            </div>
            <Title level={4} style={{ color: "#1d3557", marginBottom: 2 }}>Crear cuenta</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>Notaría Juan Carlos Cabezas Cepeda</Text>
          </div>
        )}

        <div style={{
          width: "100%", maxWidth: 420,
          background: "white", borderRadius: isMobile ? 16 : 0,
          padding: isMobile ? "28px 24px" : 0,
          boxShadow: isMobile ? "0 4px 24px rgba(0,0,0,0.08)" : "none",
        }}>
          {!isMobile && (
            <Title level={3} style={{ color: "#1d3557", marginBottom: 4 }}>Crear cuenta</Title>
          )}
          <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
            ¿Ya tienes cuenta?{" "}
            <Link to="/portal/login" style={{ color: "#457b9d", fontWeight: 600 }}>
              Inicia sesión
            </Link>
          </Text>

          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

          <Form form={form} layout="vertical" onFinish={onFinish} size="large">
            <Form.Item
              label="RUT" name="rut"
              rules={[
                { required: true, message: "Ingresa tu RUT" },
                { validator: (_, val) => val && validateRut(val) ? Promise.resolve() : Promise.reject("RUT inválido") },
              ]}
            >
              <Input placeholder="12.345.678-9" onBlur={onRutBlur} autoComplete="off" />
            </Form.Item>

            <Form.Item
              label="Email" name="email"
              rules={[
                { required: true, message: "Ingresa tu email" },
                { type: "email", message: "Email inválido" },
              ]}
            >
              <Input placeholder="tu@email.com" autoComplete="email" />
            </Form.Item>

            <Form.Item
              label="Contraseña" name="password"
              rules={[
                { required: true, message: "Ingresa una contraseña" },
                { min: 8, message: "Mínimo 8 caracteres" },
                { pattern: /(?=.*[a-zA-Z])(?=.*\d)/, message: "Debe tener al menos una letra y un número" },
              ]}
            >
              <Input.Password placeholder="Mínimo 8 caracteres" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Confirmar contraseña" name="confirm"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Confirma tu contraseña" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                    return Promise.reject("Las contraseñas no coinciden");
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Repite la contraseña" autoComplete="new-password" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary" htmlType="submit" block loading={loading}
                style={{ height: 46, background: "#1d3557", borderColor: "#1d3557", borderRadius: 8 }}
              >
                Crear cuenta
              </Button>
            </Form.Item>
          </Form>

          <Text type="secondary" style={{ fontSize: 12, display: "block", textAlign: "center", lineHeight: 1.6 }}>
            Al registrarte aceptas que tus datos sean utilizados para gestionar trámites notariales.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default PortalRegister;
